import { randomId } from './Helpers';

class Emersya {
  constructor(iframe) {
    this.iframe = iframe;
    this.animations = [];
  }

  /**
   * Wrapper for the triggerAnimation Emersya API, returns a Promise that will
   * be resolved once the animation has been completed. Read in the method why
   * the resolve can't be trusted.
   *
   * @param {number} triggerId
   * @param {string} animationName
   * @param {string|null} endState When `null` the current animation state is
   *   going to be ignored.
   * @returns {Promise}
   */
  triggerAnimation(triggerId, animationName, endState) {
    const id = randomId();

    return new Promise((resolve, reject) => {
      // If the endState is equal to the current product animation's state, we
      // resolve the promise and stop the execution.
      if (this.animations[animationName] === endState) {
        return resolve();
      }

      // Reject if Emersya doesn't get back to us within 5 seconds.
      const timeoutId = setTimeout(() => {
        reject(`triggerAnimation failed for triggerId [${triggerId}]`);
      }, 5000);

      const onMessage = event => {
        // At the end of triggerAnimation, either one is triggered. We listen
        // for both and consider the animation concluded when either one is
        // received.
        const resolveOnAction = ['onEndNodeAnimation', 'onEndCameraAnimation'];

        // The API doesn't support it but we should be able to compare the id
        // as well... "event.data.id === id" . We won't know which animation did
        // end,
        if ( ! (event && event.data && resolveOnAction.indexOf(event.data.action) !== -1)) {
          return;
        }

        window.removeEventListener('message', onMessage, false);
        clearTimeout(timeoutId);

        return resolve();
      };

      window.addEventListener('message', onMessage, false);

      this.iframe.contentWindow.postMessage({
        id,
        action: 'triggerAnimation',
        triggerId: +triggerId,
      }, '*');
    });
  };

  setCamera(position, target, up, fov, transitionTime) {
    return new Promise((resolve, reject) => {
      const id = randomId();
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', onMessage, false);
        return reject();
      }, transitionTime + 15 * 1000);

      const onMessage = event => {
        if ( ! (event && event.data && event.data.id === id)) {
          return;
        }

        // We clear the timeout, and therefore avoid calling a "reject" when we
        // get a positive event from Emersya.
        clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage, false);

        if (event.data.action === 'onSuccess') {
          return setTimeout(resolve, transitionTime);
        }

        if (event.data.action === 'onError') {
          return reject();
        }
      };

      window.addEventListener('message', onMessage, false);

      this.iframe.contentWindow.postMessage({
        id,
        action: 'setCamera',
        position,
        target,
        up,
        fov,
        transitionTime,
      }, '*');
    });
  }

  /**
   * Set the group material, an array of groupName, configurationName can be
   * provided as first parameter, this will group many API calls into the same
   * transaction.
   *
   * @param {string|Array<string, string>} groupName
   * @param {string|null} configurationName
   */
  setMaterialsGroupConfiguration(groupName, configurationName = null) {
    const postMessage = (g, c) => {
      this.iframe.contentWindow.postMessage({
        action: 'setMaterialsGroupConfiguration',
        groupName: g,
        configurationName: c,
      }, '*');
    };

    if (Array.isArray(groupName)) {
      groupName.forEach(configuration => postMessage(...configuration))
    } else {
      postMessage(groupName, configurationName);
    }
  }

  /**
   * Emersya API wrapper to update a part. If the first parameter is an array of
   * part, material we will group the setMaterialByName calls into a single
   * transaction.
   *
   * @param {string|Array<string, string>} part
   * @param {string|null} material
   */
  setMaterialByName(part, material = null) {
    this.iframe.contentWindow.postMessage({ action: 'beginTransaction' }, '*');

    const postMessage = (p, m) => {
      this.iframe.contentWindow.postMessage({
        action: 'setMaterialByName',
        materialSettings: `${p}/${m}`,
      }, '*');
    };

    if (part.constructor === Array) {
      part.forEach(settings => postMessage(...settings));
    } else {
      postMessage(part, material);
    }

    this.iframe.contentWindow.postMessage({ action: 'endTransaction' }, '*');
  }

  /**
   * Emersya API wrapper to apply a preset.
   *
   * @param {string} presetName
   */
  applyPreset(presetName) {
    return new Promise((resolve, reject) => {
      const id = randomId();
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', onMessage, false);
        return reject();
      }, 5000);

      const onMessage = event => {
        if ( ! (event && event.data && event.data.id === id)) {
          return;
        }

        // We clear the timeout, and therefore avoid calling a "reject" when we
        // get a positive event from Emersya.
        clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage, false);

        if (event.data.action === 'onSuccess') {
          return resolve();
        }

        if (event.data.action === 'onError') {
          return reject();
        }
      };

      window.addEventListener('message', onMessage, false);

      this.iframe.contentWindow
        .postMessage({ id, action: 'applyPreset', presetName }, '*');
    });
  };

  updateCustomTexture(data) {
    return new Promise((resolve, reject) => {
      const id = randomId();
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', onMessage, false);
        return reject();
      }, 5000);

      const onMessage = event => {
        if ( ! (event && event.data && event.data.id === id)) {
          return;
        }

        // We clear the timeout, and therefore avoid calling a "reject" when we
        // get a positive event from Emersya.
        clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage, false);

        if (event.data.action === 'onSuccess') {
          return resolve();
        }

        if (event.data.action === 'onError') {
          return reject();
        }
      };

      window.addEventListener('message', onMessage, false);

      this.iframe.contentWindow
        .postMessage({ action: 'updateCustomTexture', data }, '*');
    });
  }

  /**
   * Emersya API wrapper to apply a text
   *
   * @param {Object} updateCustomTexts
   * @returns {Promise}
   */
  updateCustomTexts(updateCustomTexts) {
    const id = updateCustomTexts.id;

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', onMessage, false);
        return reject();
      }, 5000);

      const onMessage = event => {
        if ( ! (event && event.data && event.data.id === id)) {
          return;
        }

        // We clear the timeout, and therefore avoid calling a "reject" when we
        // get a positive event from Emersya.
        clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage, false);

        if (event.data.action === 'onSuccess') {
          return resolve();
        }

        if (event.data.action === 'onError') {
          return reject();
        }
      };

      window.addEventListener('message', onMessage, false);

      this.iframe.contentWindow.postMessage(updateCustomTexts, '*');
    });
  }

  getScreenshot(width, height) {
    return new Promise((resolve, reject) => {
      const id = randomId();
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', onMessage, false);
        return reject();
      }, 10 * 1000);

      const onMessage = event => {
        if ( ! (event && event.data && event.data.id === id)) {
          return;
        }

        // We clear the timeout, and therefore avoid calling a "reject" when we
        // get a positive event from Emersya.
        clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage, false);

        if (event.data.action === 'onScreenshot') {
          return resolve(event.data.screenshot);
        }

        if (event.data.action === 'onError') {
          return reject();
        }
      };

      window.addEventListener('message', onMessage, false);

      this.iframe.contentWindow
        .postMessage({ id, action: 'getForcedResolutionScreenshot', width, height }, '*');
    });
  }

  getScreenshots(width, height, cameras) {
      return new Promise((resolve, reject) => {
          const id = randomId();
          const timeoutId = setTimeout(() => {
              window.removeEventListener('message', onMessage, false);
              return reject();
          }, 60 * 1000);

          const onMessage = event => {
              if ( ! (event && event.data && event.data.id === id)) {
                  return;
              }

              // We clear the timeout, and therefore avoid calling a "reject" when we
              // get a positive event from Emersya.
              clearTimeout(timeoutId);
              window.removeEventListener('message', onMessage, false);

              if (event.data.action === 'onScreenshots') {
                  return resolve(event.data.screenshots);
              }

              if (event.data.action === 'onError') {
                  return reject();
              }
          };

          window.addEventListener('message', onMessage, false);

          this.iframe.contentWindow
              .postMessage({ id, action: 'getForcedResolutionScreenshots', width, height, cameras }, '*');
      });
  }

  getCurrentMaterialTreesGroupsConfiguration() {
    return new Promise((resolve, reject) => {
      const id = randomId();
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', onMessage, false);
        return reject();
      }, 5 * 1000);

      const onMessage = event => {
        if ( ! (event && event.data && event.data.id === id)) {
          return;
        }

        // We clear the timeout, and therefore avoid calling a "reject" when we
        // get a positive event from Emersya.
        clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage, false);

        if (event.data.action === 'onCurrentGroupsConfiguration') {
          return resolve(event.data.groupsConfiguration);
        }

        if (event.data.action === 'onError') {
          return reject();
        }
      };

      window.addEventListener('message', onMessage, false);

      this.iframe.contentWindow
        .postMessage({ id, action: 'getCurrentMaterialTreesGroupsConfiguration' }, '*');
    });
  }
}

export default Emersya;



// WEBPACK FOOTER //
// ./src/Emersya.js